import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Tooltip, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import Pagination from '@material-ui/lab/Pagination';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { isNil } from 'lodash';
import { usePriorityGradingAssignments } from 'sdk';

import { ReactComponent as DeleteIcon } from 'pages/Teachers/Home/assets/delete-icon.svg';
import { ReactComponent as ErrorIcon } from 'pages/Teachers/Home/assets/error-triangle-icon.svg';
import { ReactComponent as PendingIcon } from 'pages/Teachers/Home/assets/pending-icon.svg';
import { ReactComponent as UnpublishedIcon } from 'pages/Teachers/Home/assets/unpublished-icon.svg';
import { reverse } from 'utils/urls';

import styles from './styles.module.scss';

const tooltipUseStyles = makeStyles({
  customTooltip: {
    backgroundColor: '#333867',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Nunito'
  }
});

const PriorityGradingList = () => {
  const [items, setItems] = useState([]);
  const listRef = useRef(null);

  const tooltipStyles = tooltipUseStyles();

  const [pagination, setPagination] = useState({
    numPages: 1,
    currentPage: 1,
    limit: 3
  });

  const history = useHistory();

  const {
    data: priorityGradingAssignments,
    isLoading: isLoadingPriorityGrading
  } = usePriorityGradingAssignments({
    params: {
      limit: pagination.limit,
      offset: (pagination.currentPage - 1) * pagination.limit
    }
  });

  useEffect(() => {
    if (isNil(priorityGradingAssignments)) return;

    const { count, limit } = priorityGradingAssignments;

    setPagination((prev) => ({
      ...prev,
      numPages: Math.ceil(count / limit)
    }));
  }, [priorityGradingAssignments]);

  useEffect(() => {
    if (isNil(priorityGradingAssignments)) return;
    setItems(priorityGradingAssignments.results);
  }, [priorityGradingAssignments]);

  const handleRemove = () => {};

  const changePageAndRefetchListData = (page) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      currentPage: page
    }));
  };

  console.log('priorityGradingAssignments', priorityGradingAssignments);

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollContainer} ref={listRef}>
        {isLoadingPriorityGrading && (
          <div className={styles.loadingState}>
            <Skeleton variant="rect" width="100%" height={127} />
            <Skeleton variant="rect" width="100%" height={127} />
            <Skeleton variant="rect" width="100%" height={127} />
          </div>
        )}

        {!isLoadingPriorityGrading &&
          items.map((item) => (
            <div
              key={item.id}
              className={styles.card}
              onClick={() => {
                history.push({
                  pathname: reverse(TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL, {
                    trackerInstanceSectionId: item.id
                  })
                });
              }}
            >
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{item.section_name}</h3>
                <Tooltip
                  title="Dismiss this notification to hide it permanently."
                  classes={{ tooltip: tooltipStyles.customTooltip }}
                  placement="right"
                >
                  <button
                    className={styles.closeButton}
                    onClick={() => handleRemove(item.id)}
                    aria-label="Remove item"
                  >
                    <DeleteIcon />
                  </button>
                </Tooltip>
              </div>

              <p className={styles.cardSubtitle}>{item.name}</p>

              <div className={styles.cardBadges}>
                {/* Late Badge */}
                <div className={`${styles.badge} ${styles.badgeLate}`}>
                  <span className={styles.badgeIconPlaceholder}>
                    <PendingIcon />
                  </span>
                  <span>Late ({item.late_count})</span>
                </div>

                {/* Unpublished Badge */}
                <div className={`${styles.badge} ${styles.badgeUnpublished}`}>
                  <span className={styles.badgeIconPlaceholder}>
                    <UnpublishedIcon />
                  </span>
                  <span>Unpublished ({item.unpublished_count})</span>
                </div>

                {/* Reupload Badge */}
                <div className={`${styles.badge} ${styles.badgeReupload}`}>
                  <span className={styles.badgeIconPlaceholder}>
                    <ErrorIcon />
                  </span>
                  <span>Reupload Needed ({item.needs_review_count})</span>
                </div>
              </div>
            </div>
          ))}
      </div>
      <div className={styles.pagination}>
        <Pagination
          count={pagination.numPages}
          page={pagination.currentPage}
          color="secondary"
          onChange={(event, page) => changePageAndRefetchListData(page)}
        />
      </div>
    </div>
  );
};

export default PriorityGradingList;
